// Display order = CC product template. Backend keys unchanged. "Others" = unrecognised line items only (hidden when 0).
export const PRODUCT_ORDER = ["Payments", "QR Menu", "FORA POS", "Loyalty", "FORA AI", "Videri", "Reservations", "Tablet", "Others"];

export const PRODUCT_LABEL = {
  Payments: "Payment",
  "QR Menu": "QR code",
  "FORA POS": "POS",
  Loyalty: "Loyalty",
  "FORA AI": "AI Agent",
  Videri: "Digital signage",
  Reservations: "Reservation",
  Tablet: "Tablet Menu",
  Others: "Others",
};

export const PRODUCT_SHORT = {
  Payments: "Payment",
  "QR Menu": "QR",
  "FORA POS": "POS",
  Loyalty: "Loyalty",
  "FORA AI": "AI",
  Videri: "Signage",
  Reservations: "Reserv.",
  Tablet: "Tablet",
  Others: "Others",
};

// Table always shows every product column; "Others" only when something unrecognised was sold
export const tableProducts = (products) => PRODUCT_ORDER.filter((p) => p !== "Others" || (products?.[p] || 0) > 0);

export const TABLE_HEAD = { ...PRODUCT_SHORT, Payments: "Pay" };

// Payments breakdown (provider = acquirer, channel = how the venue takes cards) – read from the line items
export const PAYMENT_PROVIDERS = ["Adyen", "Viva", "eService", "Elavon", "NLB WebPay", "Unknown"];
export const PAYMENT_CHANNELS = ["POS", "Ecom", "SoftPOS", "Unknown"];
export const PROVIDER_SHORT = { Adyen: "A", Viva: "V", eService: "eS", Elavon: "El", "NLB WebPay": "NLB", Unknown: "?" };
const ordered = (counts, order) => order.filter((k) => counts?.[k]).map((k) => [k, counts[k]]);
// "Adyen 93 · Viva 24 · Ecom 2" – providers, then the non-POS channels (POS is the default channel).
// Compact (chip / map): the two biggest providers + "other n"; full (tooltips / panel): every provider incl. Unknown.
export const paymentSplitText = (pay, { short = false, full = false } = {}) => {
  if (!pay) return "";
  let providers = ordered(pay.providers, PAYMENT_PROVIDERS);
  if (!full) {
    // compact: Unknown left out, more than three providers -> two biggest + "other n"
    providers = providers.filter(([k]) => k !== "Unknown");
    if (providers.length > 3) {
      const rest = providers.slice(2).reduce((s, [, n]) => s + n, 0);
      providers = [...providers.slice(0, 2), ["other", rest]];
    }
  }
  const parts = providers.map(([k, n]) => `${short ? PROVIDER_SHORT[k] || k : k} ${n}`);
  const channels = ordered(pay.channels, PAYMENT_CHANNELS).filter(([k]) => (full ? true : k !== "POS" && k !== "Unknown"));
  parts.push(...channels.map(([k, n]) => `${k === "Unknown" ? "channel unknown" : k} ${n}`));
  return parts.join(" · ");
};
// "Adyen POS", "Viva POS + Ecom" – one venue's / deal's payment tag
// Sales mix: what kind of thing was sold – read from the line item names (Payments fees are outside the mix)
export const SALE_KINDS = ["Licence", "Software", "Hardware", "Services"];
export const KIND_LABEL = { Licence: "Licences", Software: "Software", Hardware: "Hardware", Services: "Services" };
export const KIND_SHORT = { Licence: "Lic", Software: "SW", Hardware: "HW", Services: "Svc" };
export const salesMixText = (mix, { short = false } = {}) =>
  SALE_KINDS.filter((k) => mix?.kinds?.[k]).map((k) => `${short ? KIND_SHORT[k] : KIND_LABEL[k]} ${fmt(mix.kinds[k])}`).join(" · ");
export const paymentLabel = (tag) => (tag ? `${(tag.providers || []).join(" / ")} ${(tag.channels || []).filter((c) => c !== "Unknown").join(" + ")}`.trim() : "");

export const MAP_RAMP = ["#FECDD3", "#FCA5A5", "#F87171", "#EF4444", "#C81E1C", "#7F1D1D"];
// dark board: intensity grows from muted maroon to bright red / pink on a charcoal sea
export const MAP_RAMP_DARK = ["#5A1E1E", "#8B1F1F", "#B91C1C", "#DC2A27", "#F05252", "#FCA5A5"];
export const CLASS_BOUNDS = [1, 10, 50, 200, 500, 1000];
export const MAP_EMPTY = "#DDE3EA";
export const MAP_EMPTY_DARK = "#2A3648";
export const mapRamp = (dark) => (dark ? MAP_RAMP_DARK : MAP_RAMP);
export const mapEmpty = (dark) => (dark ? MAP_EMPTY_DARK : MAP_EMPTY);
export const UNASSIGNED_CODE = "ZZ";

export const COUNTRY_NAMES = {
  HR: "Croatia", SI: "Slovenia", PL: "Poland", RO: "Romania", BA: "Bosnia", MK: "Macedonia",
  XK: "Kosovo", AT: "Austria", RS: "Serbia", ME: "Montenegro", GR: "Greece", CY: "Cyprus", MT: "Malta",
  IT: "Italy", DE: "Germany", CH: "Switzerland", MD: "Moldova", AL: "Albania", HU: "Hungary", SK: "Slovakia",
  CZ: "Czechia", UA: "Ukraine", BG: "Bulgaria", TR: "Turkey", FR: "France", ES: "Spain", PT: "Portugal",
  NL: "Netherlands", BE: "Belgium", LU: "Luxembourg", DK: "Denmark", SE: "Sweden", NO: "Norway", FI: "Finland",
  EE: "Estonia", LV: "Latvia", LT: "Lithuania", BY: "Belarus", IE: "Ireland", GB: "United Kingdom", IS: "Iceland",
  LI: "Liechtenstein", MC: "Monaco", SM: "San Marino", AD: "Andorra", VA: "Vatican", RU: "Russia", GE: "Georgia",
  AM: "Armenia", AZ: "Azerbaijan", ZZ: "Unassigned",
};

export const ALWAYS_LABEL = ["XK", "ME", "MK", "MT", "CY"];
export const SMALL_COUNTRIES = ["XK", "ME", "MK", "MT", "CY", "SI", "AL", "LU", "LI", "MD", "MC", "SM", "AD", "VA", "BA"];

// [lon, lat] anchors so labels sit inside the visually dominant part of each country
export const LABEL_ANCHORS = {
  HR: [16.2, 45.35], SI: [14.85, 46.1], AT: [14.3, 47.6], PL: [19.4, 52.1], RO: [24.9, 45.9], XK: [20.9, 42.58],
  BA: [17.8, 44.2], MK: [21.7, 41.6], GR: [21.9, 39.5], ME: [19.3, 42.85], RS: [20.9, 44.1], MT: [14.4, 35.9],
  CY: [33.1, 35.05], IT: [12.2, 42.8], DE: [10.2, 51.1], CH: [8.2, 46.8], HU: [19.4, 47.2], SK: [19.5, 48.75],
  CZ: [15.4, 49.8], UA: [31.5, 49.2], MD: [28.6, 47.2], BG: [25.2, 42.7], AL: [20.05, 41.05], TR: [32.5, 39.2],
  FR: [2.5, 46.8], LU: [6.1, 49.8], BE: [4.6, 50.6], NL: [5.5, 52.3], DK: [9.4, 56.0], LT: [24.0, 55.3],
  LV: [25.0, 56.9], BY: [27.9, 53.5], SE: [15.5, 59.5], ES: [-3.5, 40.2], PT: [-8.1, 39.6], GB: [-1.6, 52.6],
  IE: [-8.0, 53.2], EE: [25.5, 58.7], FI: [26.0, 62.5], NO: [9.0, 61.0], GE: [43.5, 42.2], AM: [44.9, 40.3],
};

// Tiny countries get a callout label placed in open sea with a leader line
export const CALLOUTS = { HR: [13.3, 44.0], MT: [17.4, 36.4], MC: [7.4, 42.9], LI: [9.6, 45.3], SM: [14.6, 43.9], VA: [12.5, 41.9] };

export const PRODUCT_SLUG = (p) => p.toLowerCase().replace(/\s+/g, "-");

export function classIndex(count) {
  if (!count || count < 1) return -1;
  let idx = 0;
  for (let i = 0; i < CLASS_BOUNDS.length; i++) if (count >= CLASS_BOUNDS[i]) idx = i;
  return idx;
}

export function colorFor(count, dark = false) {
  const idx = classIndex(count);
  return idx < 0 ? mapEmpty(dark) : mapRamp(dark)[idx];
}

export const fmt = (n) => new Intl.NumberFormat("en-US").format(n ?? 0);

export const countryName = (code) => COUNTRY_NAMES[code] || code;

export const fmtDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");
